import React, { useContext, useEffect, useRef, useState } from 'react'

import { useScrollPosition } from '@application/Interfaces/Hooks/useScrollPosition/useScrollPosition'
import { Nullable } from '@application/Types/Opt'
import { useWidth } from '@icontainers/design-system/dist/utils'
import { BuildEnvironment } from '@icontainers/widget'
import { QuoteFormMode } from '@src/Application/Types/ComponentTypes'
import { OLIVIA_API_URL } from '@src/Core/Constants/RouteConstants'
import { Context } from '@src/Infrastructure/Context'

import { useOrganization } from '../../Hooks/useOrganization/useOrganization'
import { handleWidgetSearch, MAP_LOCALE_TO_WIDGET_LANGUAGE, resizeHandler, setStickyWidget } from './utils'
import { GhostWidgetElement, StickyWidgetWrapper, Widget, WidgetWrapper } from './WidgetElement.styled'

type WidgetElementType = {
    showQuoteForm?: boolean
    quoteFormDefaultMode: Nullable<QuoteFormMode>
    className?: string
}

export const WidgetElement = ({
    showQuoteForm,
    quoteFormDefaultMode,
    className,
}: WidgetElementType): Nullable<JSX.Element> => {
    const { isMobile } = useWidth()
    const context = useContext(Context)

    const [isSticky, setIsSticky] = useState<boolean>(false)
    const [widgetHeight, setWidgetHeight] = useState<number>(0)

    const widgetWrapperRef = useRef<HTMLDivElement>(null)
    const widgetElementRef = useRef<HTMLDivElement>(null)

    const organization = useOrganization()

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(setStickyWidget(widgetWrapperRef, isMobile, setIsSticky), [])
    useScrollPosition(setStickyWidget(widgetWrapperRef, isMobile, setIsSticky))

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(resizeHandler(setWidgetHeight, widgetElementRef?.current))
    useEffect(() => {
        window.addEventListener('resize', resizeHandler(setWidgetHeight, widgetElementRef?.current))

        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            window.removeEventListener('resize', resizeHandler(setWidgetHeight, widgetElementRef?.current))
        }
    }, [widgetElementRef])

    return showQuoteForm ? (
        <WidgetWrapper ref={widgetWrapperRef} className={className}>
            <StickyWidgetWrapper className={isSticky ? 'sticky' : ''} data-testid="stickWrapperTestId">
                <Widget
                    language={MAP_LOCALE_TO_WIDGET_LANGUAGE[context.locale]}
                    onSearch={handleWidgetSearch()}
                    ref={widgetElementRef}
                    baseApiUrl={OLIVIA_API_URL}
                    sendGA4Events
                    showMultiMode
                    showDimensionsInLCL
                    multiModeTabsPosition={isSticky ? 'LEFT' : 'TOP'}
                    defaultShipmentType={quoteFormDefaultMode || 'FCL'}
                    sendMixpanelEvents={context.sendMixpanelEvents}
                    layerIndex={1000}
                    buildEnvironment={process.env.GATSBY_BUILD_ENVIRONMENT as BuildEnvironment}
                    organizationName={organization.name}
                />
            </StickyWidgetWrapper>
            {isSticky && <GhostWidgetElement $height={widgetHeight} />}
        </WidgetWrapper>
    ) : null
}
